import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, Sparkles, ArrowRight, Clock, TrendingUp, Minus, TrendingDown, MessageCircleHeart, CornerUpRight } from 'lucide-react';
import PIELayout from './PIELayout';
import PIESkeleton from './PIESkeleton';
import { pieApi, pieCoachPreseed } from './pieApi';
import PIEUnseededEmptyState, { isUnseededError } from './PIEUnseededEmptyState';
import PIECalibrationBadge from './PIECalibrationBadge';
import { pct, pp } from './format';
import { Perf } from './PIEDisplayMode';
import './pie.css';
import logger from '../../lib/logger';

/**
 * PIEValidatePage — "Did It Work?" — closes the predict→act→validate
 * loop. Lists pending commitments (each gets a 4-slider feedback form)
 * and validated commitments (each gets a side-by-side reconciliation
 * card showing predicted ghost vs realized fill).
 */
export default function PIEValidatePage() {
  const [pending, setPending] = useState([]);
  const [validated, setValidated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const d = await pieApi.listCommitments();
      setPending(d.pending || []);
      setValidated(d.validated || []);
    } catch (e) {
      logger.error('listCommitments failed', e);
      setErr(e?.response?.data?.detail || 'Could not load commitments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const onValidated = (updated) => {
    setPending((prev) => prev.filter((c) => c.id !== updated.id));
    setValidated((prev) => [updated, ...prev]);
  };

  return (
    <PIELayout>
      <div className="pie-fade-in" data-testid="pie-validate">
        {isUnseededError(err) ? <PIEUnseededEmptyState /> : (
        <>
        <section className="pie-hero-center">
          <span className="pie-eyebrow">
            <CheckCircle2 size={12} /> Did it work?
          </span>
          <h1 className="pie-hero-h1">
            Close the loop. <span className="pie-accent-text">Honestly.</span>
          </h1>
          <p className="pie-hero-sub">
            For every action you committed to, capture what actually happened. We&apos;ll compute
            your <em>realized</em> ROE and compare it to what we predicted. Predictions
            you don&apos;t validate are just stories.
          </p>
        </section>

        {loading && (
          <div data-testid="pie-validate-skeleton" style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 12 }}>
            <PIESkeleton variant="card" height={180} />
            <PIESkeleton variant="card" height={180} />
          </div>
        )}
        {err && <div className="pie-card" style={{ color: 'var(--pie-danger)' }}>{err}</div>}

        {!loading && !err && pending.length === 0 && validated.length === 0 && (
          <EmptyState />
        )}

        {pending.length > 0 && (
          <section style={{ marginBottom: 40 }}>
            <SectionHeader
              icon={<Clock size={14} />}
              label="Pending validation"
              count={pending.length}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {pending.map((c) => (
                <PendingCard key={c.id} commitment={c} onValidated={onValidated} />
              ))}
            </div>
          </section>
        )}

        {validated.length > 0 && (
          <section>
            <SectionHeader
              icon={<CheckCircle2 size={14} />}
              label="Validated"
              count={validated.length}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {validated.map((c) => (
                <ReconciliationCard key={c.id} commitment={c} />
              ))}
            </div>
          </section>
        )}
        </>
        )}
      </div>
    </PIELayout>
  );
}

function SectionHeader({ icon, label, count }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--pie-text-secondary)' }}>
        {icon}
      </span>
      <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{label}</h2>
      <span className="pie-chip">{count}</span>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="pie-validate-empty" data-testid="pie-validate-empty">
      <div className="pie-validate-empty-icon" aria-hidden>
        <CheckCircle2 size={22} />
      </div>
      <div className="pie-validate-empty-headline">Nothing to validate yet.</div>
      <div className="pie-validate-empty-body">
        The closed loop starts on <strong>What to improve</strong> — pick the one move,
        click <em>Commit to this</em>, run it for a week, then come back here to capture
        what actually shifted.
      </div>
      <div className="pie-validate-empty-actions">
        <Link to="/mirror/improve" className="pie-btn pie-btn-primary" data-testid="pie-validate-empty-improve">
          <Sparkles size={14} /> Pick the one move <ArrowRight size={14} />
        </Link>
        <Link to="/mirror/simulate" className="pie-btn pie-btn-ghost" data-testid="pie-validate-empty-simulate">
          Or simulate first
        </Link>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Pending card — slider form
// ────────────────────────────────────────────────────────────────────

function PendingCard({ commitment, onValidated }) {
  const [skills, setSkills] = useState(0.6);
  const [motivation, setMotivation] = useState(0.6);
  const [manager, setManager] = useState(0.6);
  const [behavior, setBehavior] = useState(0.6);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      const updated = await pieApi.validateCommitment(commitment.id, {
        self_rating_skills: skills,
        self_rating_motivation: motivation,
        manager_rating: manager,
        behavior_change: behavior,
      });
      onValidated(updated);
    } catch (e) {
      logger.error('validate failed', e);
      setErr(e?.response?.data?.detail || 'Validation failed');
      setSubmitting(false);
    }
  };

  const days = relativeDays(commitment.created_at);

  return (
    <div className="pie-card pie-card-elevated" data-testid={`pie-pending-${commitment.id}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <span className="pie-eyebrow" style={{ marginBottom: 8 }}>
            <Clock size={11} /> Committed {days}
          </span>
          <h3 style={{ fontSize: 18, fontWeight: 600, margin: '8px 0 4px' }}>
            {commitment.action_label}
          </h3>
          <div style={{ fontSize: 13, color: 'var(--pie-text-muted)' }}>
            Predicted PE: <span className="pie-mono"><Perf value={commitment.predicted?.after?.pe ?? 0} kind="pct01" /></span>
            {' · '}
            Predicted ROE: <span className="pie-mono"><Perf value={(commitment.predicted?.roe?.adjusted ?? 0) * 100} kind="pct100" /></span>
          </div>
          <CommitmentTrace commitment={commitment} />
          <PulseInOwnWords commitment={commitment} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18 }}>
        <Slider
          label="My skills now feel"
          hint="(0 = no change · 1 = transformed)"
          value={skills}
          onChange={setSkills}
          testid={`pie-slider-skills-${commitment.id}`}
        />
        <Slider
          label="My motivation now feels"
          hint="(0 = lower · 1 = much higher)"
          value={motivation}
          onChange={setMotivation}
          testid={`pie-slider-motivation-${commitment.id}`}
        />
        <Slider
          label="My manager rates me"
          hint="(0 = no shift · 1 = clearly better)"
          value={manager}
          onChange={setManager}
          testid={`pie-slider-manager-${commitment.id}`}
        />
        <Slider
          label="Behavior on the job"
          hint="(0 = unchanged · 1 = visibly different)"
          value={behavior}
          onChange={setBehavior}
          testid={`pie-slider-behavior-${commitment.id}`}
        />
      </div>

      {err && (
        <div style={{ marginTop: 12, color: 'var(--pie-danger)', fontSize: 13 }}>{err}</div>
      )}

      <div style={{ marginTop: 22, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="pie-btn pie-btn-primary"
          disabled={submitting}
          onClick={submit}
          data-testid={`pie-submit-validation-${commitment.id}`}
        >
          {submitting ? 'Reconciling…' : 'Validate'}
          <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

function Slider({ label, hint, value, onChange, testid }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--pie-text)' }}>{label}</span>
        <span className="pie-mono" style={{ fontSize: 13, color: 'var(--pie-accent)', fontWeight: 600 }}>
          <Perf value={value} kind="pct01" />
        </span>
      </div>
      <input
        type="range"
        min="0"
        max="1"
        step="0.05"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={testid}
        style={{ width: '100%', accentColor: 'var(--pie-accent)' }}
      />
      <div style={{ fontSize: 11, color: 'var(--pie-text-muted)', marginTop: 2 }}>{hint}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Reconciliation card — predicted ghost vs realized fill
// ────────────────────────────────────────────────────────────────────

function ReconciliationCard({ commitment }) {
  const navigate = useNavigate();
  const predictedPE = commitment.predicted?.after?.pe ?? 0;
  const realizedPE = commitment.realized?.pe ?? 0;
  const predictedROE = commitment.predicted?.roe?.adjusted ?? 0;
  const realizedROE = commitment.realized?.roe_actual ?? 0;
  const verdict = commitment.accuracy?.verdict ?? 'met';
  const peDelta = commitment.accuracy?.pe_delta ?? 0;
  const roeDelta = commitment.accuracy?.roe_delta ?? 0;
  const calibration = commitment.predicted?.calibration;
  const calibrationApplied = calibration?.applied === true;

  const onTalkOver = () => {
    const verdictPhrase = {
      exceeded: 'and it actually exceeded my prediction',
      met: 'and the result roughly matched what was predicted',
      underperformed: "but it underperformed what was predicted",
    }[verdict] || '';
    const seed =
      `I committed to "${commitment.action_label}" ${verdictPhrase}. ` +
      `Realised PE was ${pct(realizedPE)} (predicted ${pct(predictedPE)}, Δ ${pp(peDelta)}), ` +
      `realised ROE ${(realizedROE * 100).toFixed(0)}% (predicted ${(predictedROE * 100).toFixed(0)}%). ` +
      "Help me make sense of this — what should I take away, and what's the next move?";
    pieCoachPreseed.set(seed);
    navigate('/mirror/coach');
  };

  return (
    <div className="pie-card pie-card-elevated" data-testid={`pie-validated-${commitment.id}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div>
          <span className="pie-eyebrow" style={{ marginBottom: 8 }}>
            <CheckCircle2 size={11} /> Validated {relativeDays(commitment.validated_at)}
          </span>
          <h3 style={{ fontSize: 18, fontWeight: 600, margin: '8px 0 4px' }}>
            {commitment.action_label}
          </h3>
          <CommitmentTrace commitment={commitment} />
        </div>
        <VerdictBadge verdict={verdict} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <ReconBars
          label="Personal Effectiveness (PE)"
          predicted={predictedPE}
          realized={realizedPE}
          delta={peDelta}
        />
        <ReconBars
          label="ROE (transfer factor)"
          predicted={predictedROE}
          realized={realizedROE}
          delta={roeDelta}
          calibration={calibration}
        />
      </div>

      {calibrationApplied && (
        <CalibrationHonestyNote
          calibration={calibration}
          realizedROE={realizedROE}
          predictedROE={predictedROE}
        />
      )}

      <div style={{ marginTop: 20, padding: '14px 18px', background: 'var(--pie-surface)', borderRadius: 12, fontSize: 13, color: 'var(--pie-text-secondary)', lineHeight: 1.6 }}>
        <strong style={{ color: 'var(--pie-text)' }}>What this means:</strong>{' '}
        {verdictBlurb(verdict, peDelta, roeDelta)}
      </div>

      {commitment.coach_read && (
        <div
          className="pie-coach-read"
          data-testid={`pie-coach-read-${commitment.id}`}
          style={{
            marginTop: 12,
            padding: '12px 16px',
            background: 'var(--pie-accent-soft, rgba(124, 92, 255, 0.08))',
            borderLeft: '3px solid var(--pie-accent)',
            borderRadius: 10,
            fontSize: 13,
            color: 'var(--pie-text)',
            lineHeight: 1.6,
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}
        >
          <MessageCircleHeart size={16} style={{ color: 'var(--pie-accent)', flexShrink: 0, marginTop: 2 }} aria-hidden />
          <div style={{ flex: 1 }}>{commitment.coach_read}</div>
        </div>
      )}

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="pie-btn pie-btn-ghost"
          onClick={onTalkOver}
          data-testid={`pie-talk-over-${commitment.id}`}
        >
          <MessageCircleHeart size={14} /> Talk this over with the Coach
        </button>
      </div>
    </div>
  );
}

function ReconBars({ label, predicted, realized, delta, calibration = null }) {
  // Both bars sized relative to a max of 1.0
  const predictedPct = Math.max(0, Math.min(100, predicted * 100));
  const realizedPct = Math.max(0, Math.min(100, realized * 100));
  const positive = delta > 0;
  return (
    <div>
      <div
        className="pie-score-eyebrow"
        style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
      >
        {label}
        {calibration && <PIECalibrationBadge calibration={calibration} compact />}
      </div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', height: 132 }}>
        <ReconBar pct={predictedPct} value={predicted} ghost label="Predicted" />
        <ReconBar pct={realizedPct} value={realized} label="Realized" />
      </div>
      <div style={{ marginTop: 12, fontSize: 12, color: 'var(--pie-text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
        Δ{' '}
        <span className={positive ? 'pie-delta-positive' : delta < 0 ? 'pie-delta-negative' : 'pie-delta-neutral'}>
          <Perf value={delta} kind="delta01" digits={1} />
        </span>
      </div>
    </div>
  );
}

/**
 * CalibrationHonestyNote — only renders when a commitment was created
 * with calibration applied AND has now been validated. Tells the user,
 * in one sentence, whether the audit-aware (calibrated) forecast was
 * closer to the realised number than the engine's raw prediction was.
 *
 * This is the page where users care most about whether the engine
 * was honest with them — so when the calibration loop did its job,
 * we say so out loud.
 */
function CalibrationHonestyNote({ calibration, realizedROE, predictedROE }) {
  const realisedPct = Math.round(realizedROE * 100);
  const rawPct = Math.round(predictedROE * 100);
  const calPct = Math.round(calibration.calibrated_roe_pct);
  const rawDelta = Math.abs(realisedPct - rawPct);
  const calDelta = Math.abs(realisedPct - calPct);
  const calibrationCloser = calDelta < rawDelta;
  const sameAccuracy = calDelta === rawDelta;

  let line;
  if (calibrationCloser) {
    line = (
      <>
        The audit-aware forecast nailed it closer than the raw number —
        you realised <strong>{realisedPct}%</strong>, calibrated had said{' '}
        <strong>{calPct}%</strong> (off by {calDelta}pp), the raw engine had
        said {rawPct}% (off by {rawDelta}pp). The feedback loop is doing what
        it&apos;s supposed to.
      </>
    );
  } else if (sameAccuracy) {
    line = (
      <>
        Calibrated and raw forecasts landed equally close to the real{' '}
        <strong>{realisedPct}%</strong>. Both within {calDelta}pp.
      </>
    );
  } else {
    line = (
      <>
        On this one, the raw engine was closer — you realised{' '}
        <strong>{realisedPct}%</strong>, raw had said {rawPct}% (off by{' '}
        {rawDelta}pp), calibrated had said {calPct}% (off by {calDelta}pp).
        Single-point reads like this are noisy; what matters is the trend
        over time.
      </>
    );
  }

  return (
    <div
      style={{
        marginTop: 14, padding: '12px 16px', borderRadius: 10,
        background: 'rgba(6, 182, 212, 0.08)',
        border: '1px solid rgba(6, 182, 212, 0.25)',
        fontSize: 12, color: 'var(--pie-text)', lineHeight: 1.55,
      }}
      data-testid="pie-validate-calibration-honesty"
    >
      <div
        style={{
          fontSize: 11, fontWeight: 600, color: '#0E7490', textTransform: 'uppercase',
          letterSpacing: 0.6, marginBottom: 4,
        }}
      >
        Engine honesty check
      </div>
      <div style={{ color: 'var(--pie-text-secondary)' }}>{line}</div>
    </div>
  );
}

function ReconBar({ pct, value, label, ghost = false }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, height: '100%' }}>
      <div
        style={{
          width: '100%',
          flex: 1,
          background: 'var(--pie-surface-2)',
          borderRadius: 10,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: `${pct}%`,
            background: ghost ? 'transparent' : 'var(--pie-accent)',
            border: ghost ? '2px dashed var(--pie-text-muted)' : 'none',
            borderRadius: 10,
            transition: 'height 600ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      </div>
      <div className="pie-mono" style={{ fontSize: 13, fontWeight: 600, color: ghost ? 'var(--pie-text-muted)' : 'var(--pie-text)' }}>
        <Perf value={value} kind="pct01" />
      </div>
      <div style={{ fontSize: 11, color: 'var(--pie-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
    </div>
  );
}

function VerdictBadge({ verdict }) {
  const map = {
    exceeded: { bg: '#D1FAE5', fg: '#047857', icon: <TrendingUp size={12} />, label: 'Exceeded prediction' },
    met:      { bg: '#CFFAFE', fg: '#0891B2', icon: <Minus size={12} />,      label: 'Met prediction' },
    underperformed: { bg: '#FEE2E2', fg: '#B91C1C', icon: <TrendingDown size={12} />, label: 'Underperformed' },
  };
  const v = map[verdict] || map.met;
  return (
    <span
      className="pie-chip"
      style={{ background: v.bg, color: v.fg, fontWeight: 600, fontSize: 12 }}
      data-testid={`pie-verdict-${verdict}`}
    >
      {v.icon} {v.label}
    </span>
  );
}

function verdictBlurb(verdict, peDelta, roeDelta) {
  if (verdict === 'exceeded') {
    return `You realized ${pp(peDelta)} more PE than predicted, and ROE landed ${(roeDelta * 100).toFixed(0)} percentage points above forecast. Whatever you did, do it again — and tell your coach so the model can learn from it.`;
  }
  if (verdict === 'underperformed') {
    return `Your realized PE landed ${pp(Math.abs(peDelta), { signed: false })} below prediction, and ROE was off by ${Math.abs(roeDelta * 100).toFixed(0)} percentage points. That's not a failure — it's data. Most often this means the action wasn't sized right, or the manager-support assumption was too generous.`;
  }
  return `Your prediction held up — within a normal validation band. The platform's bounded math is doing its job.`;
}

function relativeDays(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.round(ms / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
}

/**
 * CommitmentTrace — small "↗ where this came from" link on the
 * commitment header, shown only when the commitment was created
 * inside a Coach session. Clicking sets the active coach session and
 * navigates to /me/coach so the user lands ON that exact thread with
 * the full conversation history replayed.
 */
function CommitmentTrace({ commitment }) {
  const navigate = useNavigate();
  if (!commitment?.coach_session_id) return null;
  const onJump = () => {
    // Navigate with a query param; PIECoachPage reads it on mount and
    // rewires `pieCoachSession` BEFORE its history fetch fires.
    navigate(`/me/coach?session=${encodeURIComponent(commitment.coach_session_id)}`);
  };
  return (
    <button
      type="button"
      onClick={onJump}
      className="pie-commitment-trace"
      data-testid={`pie-commitment-trace-${commitment.id}`}
      title="See the Coach conversation this came from"
    >
      <CornerUpRight size={11} aria-hidden /> where this came from
    </button>
  );
}



/**
 * PulseInOwnWords — surfaces the learner's recent weekly pulse(s)
 * for THIS commitment so they can see their own narrative side-by-side
 * with the realised outcome they're about to capture. Closes the
 * prediction → pulse → validation triangle.
 *
 * Hidden when no pulses exist (empty commitments shouldn't show a
 * stub strip). When 1+ pulse exists, renders a single thin row per
 * pulse anchored on the reflection text — that's where the human
 * voice lives — with the numeric scores + friction tags as inline
 * metadata.
 */
function PulseInOwnWords({ commitment }) {
  const pulses = commitment?.pulses || [];
  if (!pulses.length) return null;
  // Most recent first — pulses come back in submission order from the
  // backend, so reverse for display.
  const ordered = [...pulses].reverse();
  return (
    <div
      style={{
        marginTop: 14, padding: '12px 14px',
        background: 'rgba(124, 92, 255, 0.06)',
        border: '1px dashed rgba(124, 92, 255, 0.3)',
        borderRadius: 10,
      }}
      data-testid={`pie-pulse-in-own-words-${commitment.id}`}
    >
      <div
        style={{
          fontSize: 10, letterSpacing: 0.6, textTransform: 'uppercase',
          color: 'var(--pie-accent)', fontWeight: 700, marginBottom: 8,
        }}
      >
        In your own words
      </div>
      {ordered.map((p) => (
        <div
          key={p.id}
          style={{
            fontSize: 12, color: 'var(--pie-text-secondary)', lineHeight: 1.55,
            marginTop: 4, paddingLeft: 4,
          }}
          data-testid={`pie-pulse-row-${p.id}`}
        >
          <span style={{ color: 'var(--pie-text)', fontWeight: 600 }}>
            {p.week_iso}:
          </span>{' '}
          {p.reflection ? (
            <>
              <span style={{ fontStyle: 'italic', color: 'var(--pie-text)' }}>
                &ldquo;{p.reflection}&rdquo;
              </span>{' '}
            </>
          ) : null}
          <span style={{ color: 'var(--pie-text-muted)' }}>
            (applied {p.behavior_application}/5 · effective {p.perceived_effectiveness}/5 · confidence {p.confidence}/5
            {p.friction && (p.friction_tags || []).length > 0 && (
              <>
                {' · '}
                <span style={{ color: '#B45309', fontWeight: 600 }}>
                  friction: {p.friction_tags.join(', ')}
                </span>
              </>
            )}
            )
          </span>
        </div>
      ))}
      <div
        style={{
          fontSize: 11, color: 'var(--pie-text-muted)',
          marginTop: 8, paddingTop: 8,
          borderTop: '1px dashed rgba(124, 92, 255, 0.2)',
        }}
      >
        Does the realised ROE square with what you said this week?
      </div>
    </div>
  );
}
